/**
 * useSparql Hook
 *
 * Custom hook for SPARQL queries with loading and error states
 * Wraps SPARQL service methods for easy component integration
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import sparqlService from '@services/sparql.service';
import type { RailMLFile, Dataset, KeyFigureSection } from '@/types';

// Get ISO year-week key (e.g. "2026-W08") for grouping files by week
const getISOWeekKey = (date: Date): string => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${weekNo}`;
};

// Mirrors the production retention policy so local dev doesn't load hundreds of files.
// Per (title, schemaVersion) group keeps: 1/day for last 7 days, 1/week for days 8–30,
// 1/month beyond 30 days.
const applyDevRetentionPolicy = (files: RailMLFile[]): RailMLFile[] => {
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const groups = new Map<string, RailMLFile[]>();
  for (const file of files) {
    const key = `${file.title}__${file.schemaVersion}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(file);
  }

  const result: RailMLFile[] = [];
  for (const group of groups.values()) {
    const sorted = [...group].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
    const seenDays = new Set<string>();
    const seenWeeks = new Set<string>();
    const seenMonths = new Set<string>();

    for (const file of sorted) {
      const date = new Date(file.created);
      if (date >= sevenDaysAgo) {
        const dayKey = date.toISOString().slice(0, 10);
        if (!seenDays.has(dayKey)) { seenDays.add(dayKey); result.push(file); }
      } else if (date >= thirtyDaysAgo) {
        const weekKey = getISOWeekKey(date);
        if (!seenWeeks.has(weekKey)) { seenWeeks.add(weekKey); result.push(file); }
      } else {
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        if (!seenMonths.has(monthKey)) { seenMonths.add(monthKey); result.push(file); }
      }
    }
  }
  return result;
};

interface UseRailMLFilesReturn {
  data: RailMLFile[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching RailML files
 * @returns { data, loading, error, refetch }
 */
export function useRailMLFiles(): UseRailMLFilesReturn {
  const [data, setData] = useState<RailMLFile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFiles = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const files = await sparqlService.getRailMLFiles();
      setData(import.meta.env.DEV ? applyDevRetentionPolicy(files) : files);
    } catch (err) {
      console.error('[useSparql] Failed to fetch RailML files:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  return {
    data,
    loading,
    error,
    refetch: fetchFiles
  };
}

interface UseDatasetsReturn {
  data: Dataset[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching DCAT Datasets
 * Re-fetches automatically when the UI language changes.
 * @returns { data, loading, error, refetch }
 */
export function useDatasets(): UseDatasetsReturn {
  const [data, setData] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { i18n } = useTranslation();
  const language = i18n.language;

  const fetchDatasets = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const datasets = await sparqlService.getDatasets(language);
      setData(datasets);
    } catch (err) {
      console.error('[useSparql] Failed to fetch datasets:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchDatasets();
  }, [fetchDatasets]);

  return {
    data,
    loading,
    error,
    refetch: fetchDatasets
  };
}

interface UseSparqlReturn {
  getRailMLFiles: () => Promise<RailMLFile[]>;
}

/**
 * Main SPARQL hook that provides direct access to SPARQL service methods
 * Use this for components that need to call SPARQL queries imperatively
 * @returns SPARQL service methods
 */
export function useSparql(): UseSparqlReturn {
  return {
    getRailMLFiles: () => sparqlService.getRailMLFiles()
  };
}

interface UseKeyFiguresReturn {
  data: KeyFigureSection[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching Key Figure sections with latest distributions.
 * Re-fetches automatically when the UI language changes.
 * @returns { data, loading, error, refetch }
 */
export function useKeyFigures(): UseKeyFiguresReturn {
  const [data, setData] = useState<KeyFigureSection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { i18n } = useTranslation();
  const language = i18n.language;

  const fetchKeyFigures = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const sections = await sparqlService.getKeyFigureSections(language);
      setData(sections);
    } catch (err) {
      console.error('[useSparql] Failed to fetch key figures:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchKeyFigures();
  }, [fetchKeyFigures]);

  return {
    data,
    loading,
    error,
    refetch: fetchKeyFigures
  };
}

export default {
  useRailMLFiles,
  useKeyFigures,
  useSparql
};
